/*
  MIGRATION_DEBT: bespoke modal layout primitives + var(--token, #hex)
  fallback patterns.

  This island defines its own <Overlay>, <Modal>, <Header>, <Body>, and
  <ErrorBanner> as inline-styled components. Many of those styles use
  `var(--token, #hex-fallback)` patterns where the hex is either dead
  (token IS defined in shared/tokens) or a real dark-mode bug (token
  is NOT defined, so the literal hex ships).

  owner: @shaun
  replace with: <Dialog> / <Modal> from shared/ui — Phase 5
  remove when: the Modal primitive ships and Compose adopts it; at that
    point every inline modal style here and the matching styles in
    ComposeForm.tsx come out together. Until then, do not piecemeal-fix
    the fallbacks here — partial cleanup creates surprise inconsistencies
    when Modal lands.

  ESLint scope note: the canonical hex-literal ban (no-restricted-
  syntax in eslint.config.js) does NOT catch `'var(--*, #hex)'` strings
  — only bare `'#hex'` literals in CSS-property positions. The bug
  here is real but is deferred to Phase 5 deliberately.
*/

import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { pickSendingBox, type BusinessSource } from '../../lib/sources'
import type { IslandProps } from '../registry'
import { ComposeForm } from './ComposeForm'

/**
 * Gmail compose island — modal triggered from the contact drawer.
 *
 * P5 of docs/proposals/gmail-sync.md. First React-island slice in the
 * gmail-sync proposal (P1-P4 were schema + backend + legacy Settings UI).
 *
 * Renders a full-screen overlay with a centered modal. Locked to a
 * single contact (the one whose drawer is open). Plain-text body with
 * optional signature pulled from user_settings.gmail.signature.
 *
 * The host page mounts this island after setting composeContext on
 * the props it passes to ShaunIslands.mount(). When the user dismisses
 * the modal — via Cancel, ESC, or successful send — the island
 * dispatches a `gmail-compose-closed` event on window.ShaunIslands.events
 * which the host listens for to call .unmount() on the handle.
 */
export default function GmailComposeIsland({ user, composeContext }: IslandProps) {
  const [accountEmail, setAccountEmail] = useState<string | null>(null)
  const [signature, setSignature] = useState<string>('')
  const [loadingConn, setLoadingConn] = useState(true)
  const [connError, setConnError] = useState<string | null>(null)

  // Bail-out states: no user, no ctx, no connection.
  useEffect(() => {
    let cancelled = false

    async function loadConnAndSettings() {
      if (!user) {
        setLoadingConn(false)
        return
      }

      try {
        const [connRes, sigRes] = await Promise.all([
          // The active business's inboxes, Gmail or Zoho Mail.
          supabase.rpc('business_sources'),
          supabase
            .from('user_settings')
            .select('setting_value')
            .eq('user_id', user.id)
            .eq('setting_key', 'gmail.signature')
            .maybeSingle(),
        ])

        if (cancelled) return

        if (connRes.error) {
          setConnError(connRes.error.message)
          setLoadingConn(false)
          return
        }
        const box = pickSendingBox((connRes.data ?? []) as BusinessSource[])
        if (!box) {
          setConnError(
            'No inbox connected. Open ⚙ Settings → Email and connect Gmail or Zoho Mail.',
          )
          setLoadingConn(false)
          return
        }
        if (box.needs_reconnect) {
          setConnError(
            `${box.email ?? 'Your inbox'} needs you to sign in again. Open ⚙ Settings → Email.`,
          )
          setLoadingConn(false)
          return
        }

        setAccountEmail(box.email)
        if (sigRes.data?.setting_value) setSignature(String(sigRes.data.setting_value))
        setLoadingConn(false)
      } catch (e) {
        if (!cancelled) {
          setConnError(e instanceof Error ? e.message : String(e))
          setLoadingConn(false)
        }
      }
    }

    void loadConnAndSettings()
    return () => {
      cancelled = true
    }
  }, [user])

  if (!user || !composeContext) return null

  const close = () => {
    const ev = new CustomEvent('gmail-compose-closed')
    window.ShaunIslands?.events.dispatchEvent(ev)
  }

  return (
    <Overlay onBackdropClick={close}>
      <Modal>
        <Header onClose={close} />
        {loadingConn ? (
          <Body>Loading Gmail connection…</Body>
        ) : connError ? (
          <Body>
            <ErrorBanner message={connError} />
          </Body>
        ) : (
          <ComposeForm
            user={user}
            ctx={composeContext}
            accountEmail={accountEmail ?? ''}
            signature={signature}
            onCancel={close}
            onSent={close}
          />
        )}
      </Modal>
    </Overlay>
  )
}

// ─── Layout primitives (inline styles so the island ships zero CSS) ──

function Overlay({
  children,
  onBackdropClick,
}: {
  children: React.ReactNode
  onBackdropClick: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onBackdropClick()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onBackdropClick])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '8vh',
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onBackdropClick()
      }}
    >
      {children}
    </div>
  )
}

function Modal({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--bg, #ffffff)',
        color: 'var(--text, #111827)',
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 12,
        width: 'min(640px, 92vw)',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  )
}

function Header({ onClose }: { onClose: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderBottom: '1px solid var(--border, #e5e7eb)',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600 }}>New email</div>
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          background: 'transparent',
          border: 'none',
          fontSize: 20,
          cursor: 'pointer',
          color: 'var(--text-muted, #6b7280)',
          lineHeight: 1,
          padding: 4,
        }}
      >
        ×
      </button>
    </div>
  )
}

function Body({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 18 }}>{children}</div>
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      style={{
        background: 'rgba(239,68,68,0.08)',
        border: '1px solid rgba(239,68,68,0.4)',
        color: 'var(--red, #dc2626)',
        padding: '10px 12px',
        borderRadius: 8,
        fontSize: 13,
        lineHeight: 1.4,
        whiteSpace: 'pre-wrap',
      }}
    >
      {message}
    </div>
  )
}
